import { z } from 'zod';
import isMongoId from '../../helpers/is-mongo-id';
import withPaginationParams from '../../helpers/with-pagination-params';
import withPaginationParamsOptional from '../../helpers/with-pagination-params-optional';
import { InvestorCustomTagCategory } from 'further-types/investor';
import { Country } from '../../../labels';
import { CalendarDay } from '../../../utils';

const listAndExportSortableColumns = [
  'birthOrIncorporation',
  'fullName',
  'email',
  'kycStatusLabel',
  'lastLoginDate',
  'firmName',
  'disableAllEmails',
] as const;

export const ListInvestorsInput = withPaginationParams(
  z.object({
    search: z.string().optional(),
    fundId: z
      .string()
      .optional()
      .refine(isMongoId({ optional: true }))
      .or(z.literal('no-investment'))
      .nullable(),
    firmId: z
      .string()
      .optional()
      .refine(isMongoId({ optional: true })),
  }),
  listAndExportSortableColumns,
);

export const ExportInvestorsInput = withPaginationParamsOptional(
  z.object({
    search: z.string().optional(),
    fundId: z
      .string()
      .optional()
      .refine(isMongoId({ optional: true })),
    firmId: z
      .string()
      .optional()
      .refine(isMongoId({ optional: true })),
  }),
  listAndExportSortableColumns,
);

export const SingleInvestorInput = z.object({
  investorId: z.string().refine(isMongoId()),
  deleteActiveInvitations: z.boolean().optional(),
});

export const ResendPlatformInvitesInput = z.object({
  investorIds: z.array(z.string().refine(isMongoId())),
});

export const SetCustomTagsInput = z.object({
  investorId: z.string().refine(isMongoId(), {
    message: 'Invalid investor ID',
  }),
  customTags: z.array(
    z.object({
      category: z.nativeEnum(InvestorCustomTagCategory),
      tag: z.string(),
    }),
  ),
});

export const UpdateDefaultEmailInput = z.object({
  investorId: z.string().refine(isMongoId()),
  furtherUserId: z.string().refine(isMongoId()),
});

export const InvestorTaxReportComponentsInput = z.object({
  investorId: z.string().refine(isMongoId()),
  taxYearStart: z.number(),
});

export const ListInvestorCustomTagsInput = z.object({
  firmId: z.string().refine(isMongoId()),
  category: z.string().optional(),
});

export const CheckInvestorUniqueInput = z.object({
  firstName: z.string(),
  lastName: z.string(),
  dob: z.string(),
  firmId: z.string().refine(isMongoId()),
  ignoreInvestorId: z.string().refine(isMongoId()).optional(),
});

export const CheckLegalEntityInvestorUniqueInput = z.object({
  registeredName: z.string(),
  firmId: z.string().refine(isMongoId()),
  ignoreInvestorId: z.string().refine(isMongoId()).optional(),
});

export const GetActiveAdviserInvitationInput = z.object({
  investorId: z.string().refine(isMongoId()),
});

export const DownloadInvestorStatementInput = z.object({
  firmId: z
    .string()
    .optional()
    .refine(isMongoId({ optional: true }), { message: 'Invalid firm ID' }),
  investorId: z
    .string()
    .optional()
    .refine(isMongoId({ optional: true }), { message: 'Invalid investor ID' }),
  valuationDate: z.string().optional(),
});

const addressSchema = z.object({
  address1: z.string().min(1, { message: 'First line of address is required' }),
  address2: z.string().optional(),
  city: z.string().min(1, { message: 'City is required' }),
  postcode: z.string().min(1, { message: 'Postcode is required' }),
  country: z.nativeEnum(Country, { message: 'Country is required' }),
  isApplyAddress: z.boolean().optional(),
});

export const CreateInvestorInput = z.object({
  firmId: z.string().refine(isMongoId(), { message: 'Firm ID is required' }),
  email: z.string().email({ message: 'Email is required and must be valid' }),
  firstName: z.string().min(1, { message: 'First name is required' }),
  lastName: z.string().min(1, { message: 'Last name is required' }),
  middleName: z.string().optional(),
  dob: z.instanceof(CalendarDay, { message: 'Date of birth is required' }),
  phoneNumber: z.string().min(1, { message: 'Phone number is required' }),
  profession: z.string().optional(),
  address: z
    .array(addressSchema)
    .min(1, { message: 'At least one address is required' }),
  isUkRemittanceTaxpayer: z.boolean().optional(),
  title: z.string().optional(),
  isClaimingTax: z.boolean().optional(),
  incomeSources: z.array(z.string()).optional(),
  nationality: z.string().optional(),
  sector: z.string().optional(),
  otherNationality: z
    .array(z.object({ country: z.string().optional() }))
    .optional(),
  signUpSource: z.string().optional(),
  externalInvestorId: z.string().optional(),
  disableAllEmails: z.boolean().optional(),
  defaultAdviserId: z.string().refine(isMongoId()).optional(),
  primaryTaxId: z.string().optional(),
  secondaryTaxId: z.string().optional(),
  taxResidencies: z
    .array(z.object({ country: z.string().optional() }))
    .optional(),
  isVulnerableClient: z.boolean().optional(),
  vulnerabilityNotes: z.string().optional(),
  isDeceased: z.boolean().optional(),
  dateOfDeath: z.instanceof(CalendarDay).optional(),
  requiresHardCopyReports: z.boolean().optional(),
  customTags: z
    .array(
      z.object({
        category: z.nativeEnum(InvestorCustomTagCategory),
        tag: z.string(),
      }),
    )
    .optional(),
});

export const CreateLegalEntityInput = z.object({
  firmId: z.string().refine(isMongoId(), { message: 'Firm ID is required' }),
  email: z.string().email({ message: 'Email is required and must be valid' }),
  registeredName: z.string().min(1, { message: 'Registered name is required' }),
  registrationNumber: z
    .string()
    .min(1, { message: 'Registration number is required' }),
  registrationDate: z.instanceof(CalendarDay, {
    message: 'Registration date is required',
  }),
  registrationState: z
    .string()
    .min(1, { message: 'Registration state is required' }),
  registeredTaxNumber: z.string().optional(),
  keyContactFirstName: z
    .string()
    .min(1, { message: 'Key contact first name is required' }),
  keyContactSurname: z
    .string()
    .min(1, { message: 'Key contact surname is required' }),
  keyContactPhoneNumber: z
    .string()
    .min(1, { message: 'Key contact phone number is required' }),
  address: z
    .array(addressSchema)
    .min(1, { message: 'At least one address is required' }),
  isUkRemittanceTaxpayer: z.boolean().optional(),
  isClaimingTax: z.boolean().optional(),
  signUpSource: z.string().optional(),
  externalInvestorId: z.string().optional(),
  disableAllEmails: z.boolean().optional(),
  defaultAdviserId: z.string().refine(isMongoId()).optional(),
  taxResidencies: z
    .array(z.object({ country: z.string().optional() }))
    .optional(),
  isVulnerableClient: z.boolean().optional(),
  vulnerabilityNotes: z.string().optional(),
  isDeceased: z.boolean().optional(),
  dateOfDeath: z.instanceof(CalendarDay).optional(),
  requiresHardCopyReports: z.boolean().optional(),
  customTags: z
    .array(
      z.object({
        category: z.nativeEnum(InvestorCustomTagCategory),
        tag: z.string(),
      }),
    )
    .optional(),
});
